import { useState, useRef } from 'react'
import { openWalletMap } from '../utils/walletSources'
import { WalletMapIcon } from './WalletMapPanel'

/**
 * WalletsPanel — the watched-wallets list as a standalone grid panel, now
 * the single home for wallet management (add moved here from the Trade
 * Tape rail). watchedWallets is owned by App (shared with TapePanel for
 * trade highlighting); this panel renders the list with rename/remove and
 * a [+ Add] affordance below the rail.
 *
 * Props:
 *   watchedWallets, setWatchedWallets
 *   addWatchedWallet            App-owned add handler (reads the inputs below)
 *   walletInput, setWalletInput
 *   walletNameInput, setWalletNameInput
 *
 * follow-alerts-20261018: each wallet has a bell. It opens two toggles,
 * "New token buys" and "Transfers out", stored on the wallet entry as
 * alerts: { buys, xfers } so they persist and sync with the list itself.
 * App's useFollowAlerts hook reads them and does the alerting.
 */
export default function WalletsPanel({
  watchedWallets = [], setWatchedWallets,
  addWatchedWallet,
  walletInput, setWalletInput,
  walletNameInput, setWalletNameInput,
}) {
  // Add form toggles open below the rail. Collapsed by default so the panel
  // stays a clean list until you reach for it.
  const [adding, setAdding] = useState(false)
  // Rename: double-click (or F2) on the name box; single click opens the map.
  const [editing, setEditing] = useState(null)
  const [alertsOpen, setAlertsOpen] = useState(null)   // address whose bell menu is open
  const toggleAlert = (address, key) => {
    setWatchedWallets(prev => prev.map(x => x.address === address
      ? { ...x, alerts: { ...(x.alerts || {}), [key]: !x.alerts?.[key] } }
      : x))
    // Browser notifications are only used when the tab is hidden; ask once.
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission()
    } catch {}
  }
  const clickTimer = useRef(null)
  const startRename = (address, box) => {
    setEditing(address)
    requestAnimationFrame(() => { const el = box?.querySelector('input'); if (el) { el.focus(); el.select() } })
  }

  const submit = () => {
    addWatchedWallet()
    // Keep the form open for rapid multi-add; App clears the inputs on success.
  }

  return (
    <div className="wallets-panel">
      <div className="wallets-panel-header">
        <span className="wallets-panel-title">Wallets</span>
      </div>

      {/* [+ Add] affordance directly below the rail */}
      <div className="wallets-add-bar">
        <button
          className="wallets-add-toggle"
          onClick={() => setAdding(a => !a)}
        >{adding ? '× Cancel' : '+ Add'}</button>
      </div>
      {adding && (
        <div className="wallets-add-form">
          <input
            type="text"
            value={walletNameInput}
            onChange={(e) => setWalletNameInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
            placeholder="Name (optional)"
            className="watchlist-input"
          />
          <div style={{ display: 'flex', gap: 4 }}>
            <input
              type="text"
              value={walletInput}
              onChange={(e) => setWalletInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
              placeholder="Paste wallet address..."
              className="watchlist-input"
              style={{ flex: 1 }}
            />
            <button className="watchlist-add-btn" onClick={submit}>+</button>
          </div>
        </div>
      )}

      <div className="wallet-watchlist-panel">
        {watchedWallets.length === 0
          ? <div className="watchlist-empty">No wallets watched — use [+ Add] above</div>
          : watchedWallets.map((w, i) => {
              const armed = !!(w.alerts?.buys || w.alerts?.xfers)
              return (
              <div key={w.address}>
              <div className="watchlist-entry" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* The whole box is the "open wallet map" button, name included.
                    Rename moved to DOUBLE-click on the box (or F2): the input is
                    read-only and click-through until then. A single click waits
                    ~220 ms so a double-click doesn't also open the map. */}
                <span className="wmap-namebox is-btn" role="button" tabIndex={0}
                  title="Open wallet map · double-click to rename" aria-label="Open wallet map"
                  onClick={() => {
                    if (editing === w.address) return
                    clearTimeout(clickTimer.current)
                    clickTimer.current = setTimeout(() => openWalletMap(w.address), 220)
                  }}
                  onDoubleClick={(e) => { clearTimeout(clickTimer.current); startRename(w.address, e.currentTarget) }}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openWalletMap(w.address) }
                    else if (e.key === 'F2') { e.preventDefault(); startRename(w.address, e.currentTarget) }
                  }}>
                <input
                  type="text"
                  defaultValue={w.label || ''}
                  placeholder={`${w.address.slice(0, 4)}...${w.address.slice(-4)}`}
                  readOnly={editing !== w.address}
                  tabIndex={editing === w.address ? 0 : -1}
                  onBlur={(e) => {
                    setEditing(null)
                    const next = e.target.value.trim()
                    if (next === (w.label || '')) return
                    setWatchedWallets(prev => prev.map((x, idx) => idx === i ? { ...x, label: next } : x))
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.target.blur()
                    else if (e.key === 'Escape') { e.target.value = w.label || ''; e.target.blur() }
                  }}
                  className="wallet-rename-input"
                  size={Math.max(6, (w.label || '').length || 11)}
                  style={{ flex: '0 1 auto', minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
                    pointerEvents: editing === w.address ? 'auto' : 'none', cursor: editing === w.address ? 'text' : 'inherit' }}
                />
                <span className="wmap-open-btn" aria-hidden="true"><WalletMapIcon /></span>
                </span>
                <span style={{ flex: 1 }} />
                <span className="watchlist-addr" title={`${w.address}\nclick for holdings + interaction map`}
                  style={{ cursor: 'pointer' }} onClick={() => openWalletMap(w.address)}>
                  {w.address.slice(0, 4)}…{w.address.slice(-4)}
                </span>
                <button
                  className="wallet-alert-bell"
                  title={armed
                    ? `Alerts on: ${[w.alerts?.buys && 'new token buys', w.alerts?.xfers && 'transfers out'].filter(Boolean).join(', ')}`
                    : 'Set alerts for this wallet'}
                  aria-label="Wallet alerts"
                  aria-expanded={alertsOpen === w.address}
                  onClick={() => setAlertsOpen(a => (a === w.address ? null : w.address))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: 12, lineHeight: 1,
                    opacity: armed ? 1 : 0.4, filter: armed ? 'none' : 'grayscale(1)' }}
                >🔔</button>
                <button className="watchlist-remove-btn" onClick={() => setWatchedWallets(prev => prev.filter((_, idx) => idx !== i))}>×</button>
              </div>
              {alertsOpen === w.address && (
                <div className="wallet-alert-opts" style={{ display: 'flex', gap: 12, padding: '2px 8px 6px 8px', fontSize: 11, color: '#9aa0a6' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!w.alerts?.buys} onChange={() => toggleAlert(w.address, 'buys')} />
                    New token buys
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!w.alerts?.xfers} onChange={() => toggleAlert(w.address, 'xfers')} />
                    Transfers out
                  </label>
                </div>
              )}
              </div>
              )
            })
        }
      </div>
    </div>
  )
}